import React from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';

import ContentTable from './ContentTable';
import { TABLE_ID_PATTERN } from './bookTableAnchor';


const BookTableNodeView = ({ node, extension, selected, deleteNode }) => {
  const tableId = node.attrs.tableId;
  const table = extension.options.resolveTable?.(tableId);

  return (
    <NodeViewWrapper
      as="div"
      data-book-table-id={tableId}
      className={`book-table-anchor ${
        selected ? 'book-table-anchor--selected' : ''
      }`}
      contentEditable={false}
    >
      {table ? (
        <ContentTable table={table} />
      ) : (
        <p className="book-table-anchor-missing">
          Tableau introuvable : {tableId}
        </p>
      )}
      <div className="book-table-anchor-actions">
        <button
          type="button"
          onClick={() => extension.options.onEditTable?.(tableId)}
        >
          Modifier le tableau
        </button>
        <button
          type="button"
          onClick={() => extension.options.onMoveTable?.(tableId)}
        >
          Déplacer le tableau
        </button>
        <button type="button" onClick={deleteNode}>
          Retirer le tableau du texte
        </button>
      </div>
    </NodeViewWrapper>
  );
};

const BookTableNode = Node.create({
  name: 'bookTable',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return {
      resolveTable: () => undefined,
      onEditTable: () => undefined,
      onMoveTable: () => undefined,
    };
  },

  addAttributes() {
    return {
      tableId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-book-table-id'),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-book-table-id]',
        getAttrs: (element) => {
          const tableId = element.getAttribute('data-book-table-id');
          if (
            element.tagName !== 'DIV' ||
            element.attributes.length !== 1 ||
            element.childNodes.length !== 0 ||
            !TABLE_ID_PATTERN.test(tableId || '')
          ) {
            return false;
          }
          return { tableId };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return [
      'div',
      mergeAttributes({
        'data-book-table-id': node.attrs.tableId,
      }),
    ];
  },

  addCommands() {
    return {
      placeBookTable:
        ({ tableId }) =>
        ({ state, tr, dispatch }) => {
          if (!TABLE_ID_PATTERN.test(tableId || '')) return false;
          const tableType = state.schema.nodes.bookTable;
          if (!tableType) return false;

          const matches = [];
          state.doc.descendants((node, position) => {
            if (node.type === tableType && node.attrs.tableId === tableId) {
              matches.push({ position, size: node.nodeSize });
            }
          });
          matches
            .sort((left, right) => right.position - left.position)
            .forEach(({ position, size }) => {
              tr.delete(position, position + size);
            });
          tr.replaceSelectionWith(tableType.create({ tableId }));
          dispatch?.(tr.scrollIntoView());
          return true;
        },
      removeBookTable:
        ({ tableId }) =>
        ({ state, tr, dispatch }) => {
          if (!TABLE_ID_PATTERN.test(tableId || '')) return false;
          const tableType = state.schema.nodes.bookTable;
          const matches = [];
          state.doc.descendants((node, position) => {
            if (node.type === tableType && node.attrs.tableId === tableId) {
              matches.push({ position, size: node.nodeSize });
            }
          });
          if (!matches.length) return false;
          matches
            .sort((left, right) => right.position - left.position)
            .forEach(({ position, size }) => {
              tr.delete(position, position + size);
            });
          dispatch?.(tr.scrollIntoView());
          return true;
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(BookTableNodeView);
  },
});

export default BookTableNode;
